import { useClickAway } from 'ahooks';
import { Badge, DropdownProps, Flex } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { ReactNode, memo, useCallback, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage, CircleSpinnerIcon } from '@/common/components';
import { useAppModalControls } from '@/common/hooks';
import { messageOnError } from '@/common/utils';

import { AddressChangerPuppeteerStatus, useUpdateAddressChangingServiceState } from '@/modules/AddressChanger';
import { SiteSearchConsoleQueryKeys, useAddRedirectingTaskMutation, useGetSitesSearchConsoleInfoQuery, useUpdateRedirectingAutoProcessingSettingMutation } from '@/modules/Domains';
import { Site } from '@/modules/Sites';

import { BaseQueryParams } from '@/common/interfaces';
import { AddressChangerBadgeView } from './AddressChangerBadgeView';

interface Props<T extends boolean> {
  siteId: string;
  isLoading: T;
  redirectingInfo: T extends false ? NonNullable<Site['searchConsoleAddressChanging']> : undefined;
  isAutoRedirecting: boolean;
}

export const AddressChangerBadgeContainer = memo(<T extends boolean>(args: Props<T>): ReactNode => {
  const { siteId, isLoading, redirectingInfo, isAutoRedirecting } = args;

  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [logsModalControl] = useAppModalControls();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [addressChangerState, setAddressChangerState] = useState<AddressChangerPuppeteerStatus | null>(null);

  const badgeRef = useRef(null);
  const dropdownWrapperRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: updateRedirectingAutoProcessingSetting } = useUpdateRedirectingAutoProcessingSettingMutation(siteId)({
    onMutate: () => {
      setIsDropdownOpen(true);
    },
    onSuccess: () => {
      appMessageApi.success('Автоматичне опрацювання було успішно змінено!');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData([SiteSearchConsoleQueryKeys.GetInfo, siteId], (data: any) => ({
        ...data,
        isAutoRedirecting: !isAutoRedirecting
      }));

      setTimeout(() => setIsDropdownOpen(false), 300);
    },
    onError: (error) => {
      setIsDropdownOpen(true);
      messageOnError(error, appMessageApi);
    }
  });

  const { mutateAsync: addRedirectingTask, isLoading: isAddingRedirectingTaskLoading } = useAddRedirectingTaskMutation(siteId)({
    onSuccess: (searchConsoleAddressChanging) => {
      queryClient.setQueryData<
        BaseQueryParams<typeof useGetSitesSearchConsoleInfoQuery>['data']
      >([SiteSearchConsoleQueryKeys.GetInfo, siteId], (data) => {
        return {
          ...data,
          searchConsoleAddressChanging
        } as BaseQueryParams<typeof useGetSitesSearchConsoleInfoQuery>['data'];
      });
      appMessageApi.success('Запит на переадресацію було успішно відправлено!');
    },
    onError: (error) => messageOnError(error, appMessageApi)
  });

  useUpdateAddressChangingServiceState({
    handleSetStateStatus: setAddressChangerState
  });

  const handleDropdownOpen = useCallback<NonNullable<DropdownProps['onOpenChange']>>((nextOpen, info) => {
    if (info.source === 'trigger' || nextOpen) {
      setIsDropdownOpen(nextOpen);
    }
  }, []);

  const handleAutoRedirectingChange = useCallback(
    async (e: CheckboxChangeEvent) => {
      await updateRedirectingAutoProcessingSetting({ isAutoRedirecting: e.target.checked });
    },
    [siteId]
  );

  const handleAddRedirectingTask = useCallback(async () => {
    await addRedirectingTask();
  }, []);

  const handleOpenLogsModal = useCallback(() => {
    logsModalControl.open();
  }, [logsModalControl]);

  useClickAway(
    (e) => {
      // @ts-ignore
      if (!(e.target?.tagName === 'svg' || e.target?.tagName === 'path' || e.target?.className === 'ant-btn-icon')) {
        handleDropdownOpen(false, { source: 'trigger' });
      }
    },
    [badgeRef, dropdownWrapperRef]
  );

  return (
    <Flex align="center">
      { (!addressChangerState) && <CircleSpinnerIcon/> }
      { (addressChangerState && addressChangerState === AddressChangerPuppeteerStatus.Opened) && <Badge status="success" dot/> }
      { (addressChangerState && addressChangerState === AddressChangerPuppeteerStatus.Idle) && <Badge status="processing" dot/> }
      { (addressChangerState && addressChangerState === AddressChangerPuppeteerStatus.Error) && <Badge status="error" dot/> }
      { (addressChangerState && addressChangerState === AddressChangerPuppeteerStatus.Closed) && <Badge status="error" dot/> }
      &nbsp;
      <AddressChangerBadgeView
        isLoading={ isLoading }
        redirectingInfo={ redirectingInfo }
        dropdownWrapperRef={ dropdownWrapperRef }
        badgeRef={ badgeRef }
        isDropdownOpen={ isDropdownOpen }
        isAutoRedirecting={ isAutoRedirecting }
        logsModalControl={ logsModalControl }
        handleAddRedirectingTask={ handleAddRedirectingTask }
        handleOpenLogsModal={ handleOpenLogsModal }
        isAddingRedirectingTaskLoading={ isAddingRedirectingTaskLoading }
        handleAutoRedirectingChange={ handleAutoRedirectingChange }
        handleDropdownOpen={ handleDropdownOpen }
      />
    </Flex>
  );
});
